import { useState, useEffect, useCallback } from "react";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../common/components/loading/loading";
import { buildConfiguredPriceDriverCombinationRows } from "../../common/services/products/productUtilities";
import { getOrganization } from "../../common/services/auth/organizationService";
import { joinArrays } from "../../common/services/utilities";
import { useTranslation } from "react-i18next";
import uuid from "react-uuid";
import Modal from "react-bootstrap/Modal";
import MarkInput from "./markInput";

const MarksTable = (props) => {
  const { t } = useTranslation(["marketdata"]);
  const [tableState, setState] = useState({
    marks: [],
    organizationNameInScope: null,
    qualityAttributeValuesByHash: {},
    isLoading: true,
  });

  const [modalState, setModalState] = useState({
    showModal: false,
    activeMark: null,
    termId: null,
    termName: null,
    rowData: null,
    isNew: true,
    canEdit: true,
  });

  const {
    organizationId,
    marketId,
    marketDate,
    priceBasisId,
    currencyId,
    uomId,
    pricePrecision,
    currencySymbol,
    allowEdit,
    allowOverrideMarks,
    product,
    marketPriceDriverCombinations,
    marks,
    terms,
    submitCallbackFunction,
  } = props;

  // Custom cell renderer for mark values
  const MarkValueRenderer = useCallback(
    (params) => {
      // Format price for display
      const formatPrice = (price) => {
        if (!price && price !== 0) return "";
        return currencySymbol + Number(price).toFixed(pricePrecision);
      };

      // Format bid/offer for display
      const formatBidOffer = (bid, offer) => {
        const bidStr =
          bid !== null && bid !== undefined ? formatPrice(bid) : "";
        const offerStr =
          offer !== null && offer !== undefined ? formatPrice(offer) : "";
        if (!bidStr && !offerStr) return "-";
        return `${bidStr}/${offerStr}`;
      };

      const termId = params.colDef.field;
      const mark = params.data[termId];

      let displayValue = "-";
      let textColor = "var(--theme-header)";

      if (mark) {
        if (mark.price !== null && mark.price !== undefined) {
          displayValue = formatPrice(mark.price);
        } else {
          displayValue = formatBidOffer(mark.bid, mark.offer);
        }

        if (mark.markState === "Approved") {
          textColor = "var(--theme-green)";
        } else if (mark.markState === "Rejected") {
          textColor = "var(--theme-red)";
        }
      }

      const isEmptyValue = displayValue === "-";

      return (
        <div
          className={`mark-cell clickable${isEmptyValue ? " empty-value" : ""}`}
          style={{ color: textColor }}
        >
          {displayValue}
        </div>
      );
    },
    [currencySymbol, pricePrecision]
  );

  // Join marks data
  const joinMarks = useCallback((pivotedData, marks) => {
    const groupedByQualityAttributeValueHash = marks.reduce((result, obj) => {
      const { qualityAttributeValueHash, termId, ...rest } = obj;

      if (!result[qualityAttributeValueHash]) {
        result[qualityAttributeValueHash] = {};
      }

      result[qualityAttributeValueHash][termId] = {
        ...rest,
        qualityAttributeValueHash: qualityAttributeValueHash,
      };

      return result;
    }, {});

    const pivotedMarks = Object.entries(groupedByQualityAttributeValueHash).map(
      ([qualityAttributeValueHash, terms]) => ({
        qualityAttributeValueHash,
        ...terms,
      })
    );

    return joinArrays(
      pivotedData,
      pivotedMarks,
      "qualityAttributeValueHash"
    );
  }, []);

  // Initialize the table
  const initialize = useCallback(async () => {
    try {
      if (!product) return;

      const combinationRows = buildConfiguredPriceDriverCombinationRows(
        product,
        marketPriceDriverCombinations
      );
      const joinedData = joinMarks(combinationRows, marks);
      const organization = await getOrganization(organizationId);

      setState({
        marks: joinedData,
        qualityAttributeValuesByHash: joinedData.reduce((qavByHash, jd) => {
          qavByHash[jd.qualityAttributeValueHash] = jd.qualityAttributeValues;
          return qavByHash;
        }, {}),
        organizationNameInScope: organization.name,
        isLoading: false,
      });
    } catch (error) {
      console.error("Error initializing table:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, [
    product,
    marketPriceDriverCombinations,
    marks,
    organizationId,
    joinMarks,
  ]);

  // Handle cell click to open modal
  const handleCellClicked = useCallback(
    (params) => {
      const colDef = params.colDef;
      const data = params.data;

      // Only handle term columns (not product name or attributes)
      if (!terms || !terms.find((term) => term.termId === colDef.field)) {
        return;
      }

      const termId = colDef.field;
      const term = terms.find((t) => t.termId === termId);
      if (!term) return;

      const termName = term.name;
      // Check if this is a new mark or existing mark
      const isNew = !data[termId];
      const activeMark = data[termId] || {
        markId: uuid(),
        eventId: uuid(),
        qualityAttributeValueHash: data.qualityAttributeValueHash,
        active: true,
      };

      // Check if user can edit based on mark state (same logic as original)
      const canEdit = activeMark.markState === "Approved" ? false : allowEdit;

      setModalState({
        showModal: true,
        activeMark,
        termId,
        termName,
        rowData: data,
        isNew: isNew,
        canEdit,
      });
    },
    [terms, allowEdit]
  );

  // Handle modal close
  const handleModalClose = () => {
    setModalState((prev) => ({
      ...prev,
      showModal: false,
    }));
  };

  // Handle mark update - this needs to properly handle the response
  const handleMarkUpdate = async (mark) => {
    try {
      // If parent provided a submit callback, use it and wait for response
      if (submitCallbackFunction) {
        // Pass the mark to the parent's callback and get response
        const response = await submitCallbackFunction(mark);

        // Close the modal
        handleModalClose();

        // Return the response so MarkInput can process it
        return (
          response || {
            markId: mark.markId,
            success: true,
            errors: null,
          }
        );
      } else {
        // No callback provided, just close the modal
        handleModalClose();

        // Return a default response
        return {
          markId: mark.markId,
          success: true,
          errors: null,
        };
      }
    } catch (error) {
      console.error("Error submitting mark:", error);

      // Return error format expected by MarkInput
      return {
        success: false,
        errors: {
          title: error.message || "Error submitting mark",
        },
      };
    }
  };

  // Effect for initialization
  useEffect(() => {
    initialize();
  }, [
    marketDate,
    priceBasisId,
    currencyId,
    uomId,
    organizationId,
    marks,
    terms,
    initialize,
  ]);

  // Column definitions
  const getColumnDefs = useCallback(() => {
    if (!product || !terms) return [];

    return [
      {
        headerName: t("MarketDataProduct"),
        field: "shortName",
      },
      ...product.qualityAttributes
        .filter((qualityAttribute) => qualityAttribute.isPriceDriver !== false)
        .map((qualityAttribute) => ({
          headerName: qualityAttribute.qualityAttributeName,
          field: qualityAttribute.qualityAttributeId,
        })),
      ...terms
        .sort((a, b) => (new Date(a.endDate) > new Date(b.endDate) ? 1 : -1))
        .map((term) => ({
          headerName: term.name,
          field: term.termId,
          cellClass: "clickable",
          cellRenderer: MarkValueRenderer,
          cellDataType: false,
        })),
    ];
  }, [product, terms, t, MarkValueRenderer]);

  const autoSizeGridColumns = useCallback((params) => {
      params?.columnApi?.autoSizeAllColumns?.(false);
      params?.api?.autoSizeAllColumns?.(false);
    }, []);
    
  if (tableState.isLoading) return <Loading />;

  return (
    <div className="container-fluid">
      <ThemeableAgGrid
        className="table-small"
        columnDefs={getColumnDefs()}
        defaultColDef={{
          sortable: false,
          resizable: true,
          type: "centerAligned",
          filter: false,
          wrapText: false,
          autoHeight: true,
          wrapHeaderText: false,
          autoHeaderHeight: true,
        }}
        autoSizeStrategy={{
          type: "fitCellContents",
          skipHeader: false,
          defaultMinWidth: 0,
        }}
        rowData={tableState.marks}
        onCellClicked={handleCellClicked}
        domLayout="autoHeight"
        animateRows={true}
        getRowId={(params) => params.data.qualityAttributeValueHash}
        allowHorizontalScroll={true}
        onFirstDataRendered={autoSizeGridColumns}
        onRowDataUpdated={autoSizeGridColumns}
        onGridSizeChanged={autoSizeGridColumns}
      />

      {/* Modal for editing marks */}
      <Modal
        show={modalState.showModal}
        onHide={handleModalClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalState.isNew ? (
              <p>{t("AddMark")}</p>
            ) : modalState.canEdit ? (
              <p>{t("UpdateMark")}</p>
            ) : (
              <p>{t("ViewMark")}</p>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalState.showModal && modalState.rowData && (
            <MarkInput
              shortName={modalState.rowData.shortName}
              termName={modalState.termName}
              organizationId={organizationId}
              marketId={marketId}
              termId={modalState.termId}
              priceBasisId={priceBasisId}
              currencyId={currencyId}
              uomId={uomId}
              marketDate={marketDate}
              qualityAttributeValues={modalState.rowData.qualityAttributeValues}
              mark={modalState.activeMark}
              allowEdit={modalState.canEdit}
              allowOverrideMarks={allowOverrideMarks}
              onSave={handleModalClose}
              isNew={modalState.isNew}
              pricePrecision={pricePrecision}
              currencySymbol={currencySymbol}
              submitCallbackFunction={handleMarkUpdate}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MarksTable;
